import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import AppShell from '../components/AppShell';
import ReadFailed from '../components/ReadFailed';
import LegalLinks from '../components/LegalLinks';
import Tabs from '../components/Tabs';
import StudioProfileCard from '../components/settings/StudioProfileCard';
import NamingFormatPanel from '../components/settings/NamingFormatPanel';
import InvoicingPanel from '../components/settings/InvoicingPanel';
import MessagingPanel from '../components/settings/MessagingPanel';
import MembershipPanel from '../components/settings/MembershipPanel';
import MobileAppPanel from '../components/settings/MobileAppPanel';
import DeleteAccountPanel from '../components/settings/DeleteAccountPanel';
import EventSchedulingPanel from '../components/settings/EventSchedulingPanel';
import MileagePanel from '../components/settings/MileagePanel';
import PoliciesPanel from '../components/settings/PoliciesPanel';
import FamilyPortalSettingsPanel from '../components/settings/FamilyPortalSettingsPanel';
import FamilyPortalActivity from '../components/settings/FamilyPortalActivity';
import StudioProfileDrawer from '../components/settings/StudioProfileDrawer';
import { useSettings } from '../data/useSettings';
import { useExpenses } from '../data/useExpenses';
import { useMileage } from '../data/useMileage';
import { useCategories } from '../data/useCategories';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import {
  mileageSettingsOf, policiesOf, portalVisibilityOf, teacherNoticesOf,
} from '../domain/studio';
import { familyPortalOpen } from '../domain/familyPortal';
import { teacherDisplayName, teacherFullName } from '../domain/people';
import useMileageExpenseSettings from '../hooks/useMileageExpenseSettings';
import s from '../components/settings/settings.module.css';

// Business Settings.
//
// As in MMS, the General screen is two columns: a profile card on the left
// (logo, studio name, region, time zone) with an "Edit Business Profile"
// button, and a tabbed panel on the right whose General tab holds Event
// Scheduling and Naming Format.
//
// ── No Save button, a knowing difference from MMS ──────────────────────────
//
// MMS ends each settings panel with [Discard] [Save Changes], and its Edit
// Business Profile drawer with [Cancel] [Save] disabled until dirty. Here a
// radio, a checkbox or a select writes the moment it changes, and a typed
// field when it is left (useCommitted.js is the one implementation).
//
// That is the data layer's concurrency model applied to settings: a Save
// button models a client holding the authoritative copy and publishing it.
// `core/src/reconcile.rs` classes this collection `perFieldMerge`: a settings
// write is a merging set of the fields the patch named (#337), so the smaller
// the write, the less a second device loses to it. The cost is Discard; the
// top of StudioProfileDrawer.jsx has the rest.
//
// There is no "Saved" moment on screen; a settings write shows on the sync
// chip as any write does.

// 'Mileage Log' is MMS's tab of that name (#520). MMS's tabs: General,
// Membership, Accounts & Invoices, Online Payments, Email & SMS, Policies,
// Student Portal, Mileage Log, Sales Tax, Integrations (#554). Policies (#766)
// follows Email & SMS, as in MMS, and Family Portal (#774) follows Policies,
// where MMS has its Student Portal tab; it holds that tab's Visibility group.
// Each family's access is on the student's own Family Portal tab (#770).
// Mobile app is ours, and Membership sits last, with the teacher's account
// rather than the studio's settings.
//
// Policies and Family Portal are drawn only while the Family Portal is open
// (#769, core/src/family_portal.rs): every setting on them is the portal's.
// While it is closed the weekly hold box is on General → Event Scheduling, so
// a teacher has it either way.
const PORTAL_TABS = ['Policies', 'Family Portal'];
const ALL_TABS = ['General', 'Accounts & Invoices', 'Email & SMS', ...PORTAL_TABS, 'Mileage Log', 'Mobile app', 'Membership'];

function settingsTabs() {
  return familyPortalOpen() ? ALL_TABS : ALL_TABS.filter((t) => !PORTAL_TABS.includes(t));
}

// Which tab a link may open directly.
//
// The trial banner and the paywall both land a teacher on Membership, and the
// tab is local state rather than a route, so the hash is the seam. One entry
// rather than a mapping over the tabs: only Membership is linked to from
// outside this page.
const TAB_HASHES = { '#membership': 'Membership' };

// The tab a hash asks for, or General when it asks for nothing we know about.
function tabForHash(hash, TABS) {
  const wanted = TABS.indexOf(TAB_HASHES[hash] || '');
  return wanted === -1 ? 0 : wanted;
}

// The Mileage Log tab's data, read only while the tab is open: the trips its
// "update the existing entries" writes an expense for, and the expense
// categories its select offers.
function MileageTab({ settings, saveStudio }) {
  const { countExistingExpenses, updateExistingExpenses } = useMileage();
  const { categoriesFor } = useExpenses();
  const { distanceUnit, currency, locale } = useLocale();
  const expense = useMileageExpenseSettings();
  return (
    <MileagePanel
      mileage={mileageSettingsOf(settings)}
      unit={distanceUnit}
      currency={currency}
      locale={locale}
      categories={categoriesFor('expense')}
      // How many trip dates from Starting On the update writes (#554).
      countFrom={(from) => countExistingExpenses(expense, from)}
      // A patch of the leaves that changed, like every panel here.
      onChange={(patch) => saveStudio({ mileage: patch })}
      onUpdateExisting={(from) => updateExistingExpenses(expense, from)}
    />
  );
}

// The Policies tab (#766), with the studio's event categories for its
// Cancelled Event Category select.
function PoliciesTab({ settings, saveStudio }) {
  const { categories } = useCategories();
  return (
    <PoliciesPanel
      policies={policiesOf(settings)}
      scheduling={settings.scheduling}
      notices={teacherNoticesOf(settings)}
      categories={categories}
      // A patch of the leaves that changed, like every panel here.
      onChange={(patch) => saveStudio({ policies: patch })}
      onChangeScheduling={(patch) => saveStudio({ scheduling: patch })}
      onChangeNotices={(patch) => saveStudio({ teacherNotices: patch })}
    />
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const {
    settings, named, saveStudio, loading, failed,
  } = useSettings();
  const { hash } = useLocation();
  // As the initial value, so a teacher arriving from the trial banner sees
  // Membership on the first paint rather than General for a frame.
  const TABS = settingsTabs();
  const [tab, setTab] = useState(() => tabForHash(hash, TABS));
  const [editing, setEditing] = useState(false);

  // And again when the hash changes: a teacher already on Settings who clicks
  // the banner gets a hash change and no mount.
  //
  // Keyed on the hash rather than run every render, so clicking General, which
  // does not change the URL, is not dragged back. An unrecognised hash is left
  // alone, so an anchor somebody else adds does not move the tabs.
  React.useEffect(() => {
    const wanted = TABS.indexOf(TAB_HASHES[hash] || '');
    if (wanted !== -1) setTab(wanted);
  }, [hash]);

  return (
    <AppShell title="Business Settings">
      <div className={s.page}>
        <div className={s.columns}>
          <StudioProfileCard
            settings={settings}
            named={named}
            // The teacher in the Teacher Name Format (#797), the one place on
            // this page the format shows.
            teacherName={loading ? null : teacherDisplayName(settings, user?.displayName)}
            loading={loading}
            onEdit={() => setEditing(true)}
          />

          <div className={s.right}>
            <Tabs tabs={TABS} current={tab} onChange={setTab} />
            {/* The record's read stopped before it answered (#647). */}
            {loading && failed ? <ReadFailed /> : null}
            {/* Nothing to touch until the record has been read: a control
                offered before the first snapshot would write the defaults over
                what the studio had stored (the guard in useSettings.saveStudio).

                Compared by name rather than by index, like the tabs below, so a
                tab inserted before it does not move the panel. */}
            {!loading && TABS[tab] === 'General' ? (
              <>
                {/* Event Scheduling above Naming Format, which is the order MMS
                    puts them in on this tab. */}
                <EventSchedulingPanel
                  scheduling={settings.scheduling}
                  onChange={(patch) => saveStudio({ scheduling: patch })}
                  // The weekly hold box, while Policies is not drawn (#769).
                  withHold={!familyPortalOpen()}
                />
                <NamingFormatPanel
                  nameFormats={settings.nameFormats}
                  // A patch of the one role that changed, like every other
                  // panel here — nameFormats is a merged group (GROUPS in
                  // data/useSettings.jsx), so handing over the whole object
                  // would carry this render's stale copy of the other two.
                  onChange={(patch) => saveStudio({ nameFormats: patch })}
                />
              </>
            ) : null}
            {/* Compared by name rather than by index, so a tab inserted before
                it does not move the panel. */}
            {!loading && TABS[tab] === 'Accounts & Invoices' ? (
              <InvoicingPanel
                invoicing={settings.invoicing}
                onChange={(patch) => saveStudio({ invoicing: patch })}
                // The overdue-reminder controls sit on this tab because MMS
                // puts them here, but they are stored in the `messaging`
                // group — see the note above InvoicingPanel.
                messaging={settings.messaging}
                onChangeMessaging={(patch) => saveStudio({ messaging: patch })}
                // Both only for the Email Timeframe: the locale decides whether
                // its hours read "8:00 AM" or "08:00", and the zone is named
                // under the control because the hours are the studio's own clock
                // and nothing else on this tab is.
                locale={settings.locale}
                timeZone={settings.timeZone}
                // Invoice Formatting shows the studio's logo too, as MMS's
                // does. It is one top-level field, not an `invoicing` leaf, so
                // it is saved as the profile drawer saves it.
                logo={settings.logo || null}
                onChangeLogo={saveStudio}
              />
            ) : null}
            {!loading && TABS[tab] === 'Email & SMS' ? (
              <MessagingPanel
                messaging={settings.messaging}
                makeup={settings.makeup}
                // What the sending-identity preview puts in the display name:
                // the record's own name, not studioDisplayName's "Your Studio"
                // stand-in, which would describe an envelope no unnamed studio
                // should expect.
                studioName={settings.name}
                onChangeMessaging={(patch) => saveStudio({ messaging: patch })}
                onChangeMakeup={(patch) => saveStudio({ makeup: patch })}
              />
            ) : null}
            {!loading && TABS[tab] === 'Policies' ? (
              <PoliciesTab settings={settings} saveStudio={saveStudio} />
            ) : null}
            {!loading && TABS[tab] === 'Family Portal' ? (
              <FamilyPortalSettingsPanel
                visibility={portalVisibilityOf(settings)}
                // A patch of the leaf that changed, like every panel here.
                onChange={(patch) => saveStudio({ portalVisibility: patch })}
              />
            ) : null}
            {/* What families did in the portal (#773), beside its settings. */}
            {!loading && TABS[tab] === 'Family Portal' ? (
              <FamilyPortalActivity timeZone={settings.timeZone} />
            ) : null}
            {!loading && TABS[tab] === 'Mileage Log' ? (
              <MileageTab settings={settings} saveStudio={saveStudio} />
            ) : null}
            {/* Not gated on `loading`, unlike the panels above, which write the
                studio record. This panel writes nothing — firestore.rules
                refuses every client write to both billing paths — and reads a
                different pair of documents, so it holds its own "not read yet"
                state (see MembershipPanel). */}
            {/* Not gated on `loading` either: this panel writes no studio
                settings and reads none, so waiting on that snapshot would only
                delay a teacher with a new phone. */}
            {TABS[tab] === 'Mobile app' ? <MobileAppPanel /> : null}
            {TABS[tab] === 'Membership' ? <MembershipPanel /> : null}
            {/* Deleting the account (#432) sits under the subscription, on the
                tab about the teacher's relationship with us. Outside
                MembershipPanel so its "checking your subscription" gate does
                not hide the way out. */}
            {TABS[tab] === 'Membership' ? <DeleteAccountPanel /> : null}
          </div>
        </div>
        {/* Privacy and Terms, at the foot of the page under every tab: about
            us rather than the studio, and quieter than any panel. */}
        <LegalLinks />
      </div>

      <StudioProfileDrawer
        open={editing}
        settings={settings}
        // MMS defaults Studio Name to the teacher's own name: First Last,
        // from the stored pair or the sign-in's display name (#797).
        defaultName={teacherFullName(settings, user?.displayName) || ''}
        signInName={user?.displayName || null}
        onClose={() => setEditing(false)}
        // Each field of the profile arrives as its own patch, as the panels'
        // do.
        onChange={saveStudio}
      />
    </AppShell>
  );
}
